import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, Loader2, ExternalLink, Eye, RefreshCw, Globe, MapPin, Languages, Tags, Tag, X, Plus, FolderKanban } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { serpApi, projectsApi, keywordGroupsApi, kwReportApi, type SerpRequest, type SerpResult, type Project, type AnalysisAccountProfile, type KeywordGroup } from "@/api";
import JobProgressCard from "@/components/JobProgressCard";
import { MultiSelectFilter } from "@/components/MultiSelectFilter";
import { LOCATION_OPTIONS, LANGUAGE_OPTIONS, DEFAULT_LOCATION, DEFAULT_LANGUAGE } from "@/lib/serp-constants";
import PipelineOptions, { getDefaultStages } from "@/components/PipelineOptions";

export default function KeywordSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawMode = searchParams.get('mode');
  const mode = (rawMode === 'batch' ? 'batch' : 'single') as "single" | "batch";
  const setMode = (m: "single" | "batch") => setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('mode', m); return p; }, { replace: true });
  // Search form
  const [keyword, setKeyword] = useState("");
  const [batchKeywords, setBatchKeywords] = useState("");
  const [locationCode, setLocationCode] = useState(DEFAULT_LOCATION.toString());
  const [languageCode, setLanguageCode] = useState(DEFAULT_LANGUAGE);
  const [targetUrl, setTargetUrl] = useState("");

  // Project inline create
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectAccount, setNewProjectAccount] = useState("__system_default__");

  // Group & Tags
  const [groupId, setGroupId] = useState("__none__");
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [newTagInput, setNewTagInput] = useState("");
  const [pipelineStages, setPipelineStages] = useState<string[]>(getDefaultStages('keyword'));

  // Filters
  const [projectId, setProjectId] = useState("__all__");
  const [statusMultiFilter, setStatusMultiFilter] = useState<Set<string>>(new Set());
  const [keywordFilter, setKeywordFilter] = useState("");

  // APP16 four-dimension filter state
  const [filterApp16Project, setFilterApp16Project] = useState('');
  const [filterItem, setFilterItem] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [app16Projects, setApp16Projects] = useState<{id:string;name:string}[]>([]);
  const [app16Items, setApp16Items] = useState<{id:string;item_name:string;item_type:string}[]>([]);
  const [app16Groups, setApp16Groups] = useState<{id:string;path:string}[]>([]);
  const [app16Tags, setApp16Tags] = useState<{id:string;name:string}[]>([]);

  // Expand
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // KW Report pipeline
  const [kwReportJobId, setKwReportJobId] = useState<string | null>(null);
  const [kwReportKeyword, setKwReportKeyword] = useState<string>("");
  const queryClient = useQueryClient();

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ["projects"],
    queryFn: async () => { const r = await projectsApi.list(); return r.data || []; },
  });

  const pid = projectId !== "__all__" ? projectId : undefined;

  const { data: groups = [] } = useQuery<KeywordGroup[]>({
    queryKey: ["keyword-groups", pid],
    queryFn: async () => {
      if (!pid) return [];
      return (await keywordGroupsApi.list(pid)).data || [];
    },
    enabled: !!pid,
  });

  const { data: projectTags = [] } = useQuery<string[]>({
    queryKey: ["project-tags", pid],
    queryFn: async () => {
      if (!pid) return [];
      return (await keywordGroupsApi.allTags(pid)).data || [];
    },
    enabled: !!pid,
  });

  const { data: serpStatus } = useQuery({
    queryKey: ["serp-status"],
    queryFn: async () => { const r = await serpApi.status(); return r.data; },
  });

  const { data: accountProfiles = [] } = useQuery<AnalysisAccountProfile[]>({
    queryKey: ["analysis-account-profiles"],
    queryFn: async () => (await projectsApi.analysisAccountProfiles()).data || [],
  });

  const selectedProject = projects.find(project => project.project_id === projectId);
  const selectedProfile = accountProfiles.find(profile => selectedProject?.analysis_account_profile_key ? profile.accountKey === selectedProject.analysis_account_profile_key : profile.isDefault === true);
  const accountLabel = selectedProject ? (selectedProfile ? (selectedProfile.accountKey === "company" ? "Company" : "Personal") : "Unavailable") : (selectedProfile ? "System Default" : "Unavailable");
  const llmLabel = selectedProfile?.llmProfileKey === "cometapi_company" ? "CometAPI" : selectedProfile?.llmProfileKey === "openai_personal" ? "OpenAI" : "Unavailable";
  const analysisReady = selectedProfile?.dataforseoConfigured === true && selectedProfile.llmConfigured === true;
  const requestAccountLabel = (request: SerpRequest) => request.dataforseo_profile_key === "dataforseo_company" ? "Company" : request.dataforseo_profile_key === "dataforseo_personal" ? "Personal" : "Legacy/Unknown";

  const { data: serpStats } = useQuery({
    queryKey: ["serp-stats"],
    queryFn: async () => { const r = await serpApi.stats(); return r.data; },
    refetchInterval: 5000,
  });

  const { data: requests = [], isLoading } = useQuery<SerpRequest[]>({
    queryKey: ["serp-requests", projectId],
    queryFn: async () => {
      const r = await serpApi.requests({ limit: 200, project_id: projectId === "__all__" ? undefined : projectId });
      return r.data || [];
    },
    refetchInterval: 5000,
  });

  const { data: expandedResults = [] } = useQuery<SerpResult[]>({
    queryKey: ["serp-results", expandedId],
    queryFn: async () => {
      if (!expandedId) return [];
      const r = await serpApi.getRequest(expandedId);
      return r.data?.results || [];
    },
    enabled: !!expandedId,
  });

  const createProjectMutation = useMutation({
    mutationFn: () => projectsApi.create({
      name: newProjectName.trim(),
      analysis_account_profile_key: newProjectAccount === "__system_default__" ? null : newProjectAccount as "personal" | "company",
    }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      const id = res.data?.project_id;
      if (id) setProjectId(id);
      toast.success(`專案「${newProjectName.trim()}」已建立`);
      setNewProjectName(""); setNewProjectAccount("__system_default__"); setShowNewProject(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const createGroupMutation = useMutation({
    mutationFn: () => keywordGroupsApi.create({ project_id: projectId === "__all__" ? "" : projectId, name: newGroupName.trim() }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["keyword-groups", pid] });
      const id = res.data?.group_id;
      if (id) setGroupId(id);
      toast.success(`群組「${newGroupName.trim()}」已建立`);
      setNewGroupName(""); setShowNewGroup(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Save keywords to group (fire-and-forget)
  const saveToGroup = async (keywords: string[]) => {
    if (groupId === "__none__" || keywords.length === 0) return;
    try {
      await keywordGroupsApi.addItems(groupId, {
        keywords,
        tags: selectedTags.length ? selectedTags : undefined,
      });
      queryClient.invalidateQueries({ queryKey: ["keyword-group"] });
      queryClient.invalidateQueries({ queryKey: ["project-tags"] });
    } catch { /* silent */ }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const addNewTag = () => {
    const tag = newTagInput.trim();
    if (tag && !selectedTags.includes(tag)) {
      setSelectedTags(prev => [...prev, tag]);
    }
    setNewTagInput("");
  };

  const analyzeMutation = useMutation({
    mutationFn: () => serpApi.analyze({
      keyword: keyword.trim(),
      location_code: parseInt(locationCode),
      language_code: languageCode,
      project_id: pid,
      target_url: targetUrl.trim() || undefined,
      auto_scrape: true,
      selected_stages: pipelineStages.length > 0 ? pipelineStages : undefined,
      analysis_account_profile_key: selectedProject?.analysis_account_profile_key,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serp-requests"] });
      queryClient.invalidateQueries({ queryKey: ["serp-stats"] });
      toast.success(`SERP 分析已提交：${keyword}`);
      saveToGroup([keyword.trim()]);
      setKeyword("");
    },
    onError: (err: Error) => toast.error(`分析失敗: ${err.message}`),
  });

  const batchMutation = useMutation({
    mutationFn: (keywords: string[]) => serpApi.batchAnalyze({
      keywords,
      location_code: parseInt(locationCode),
      language_code: languageCode,
      project_id: pid,
      target_url: targetUrl.trim() || undefined,
      selected_stages: pipelineStages.length > 0 ? pipelineStages : undefined,
      analysis_account_profile_key: selectedProject?.analysis_account_profile_key,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serp-requests"] });
      queryClient.invalidateQueries({ queryKey: ["serp-stats"] });
      const kws = batchKeywords.split("\n").map(k => k.trim()).filter(Boolean);
      toast.success(`批次 SERP 分析已提交 (${kws.length} 個)`);
      saveToGroup(kws);
      setBatchKeywords("");
    },
    onError: (err: Error) => toast.error(`批次分析失敗: ${err.message}`),
  });

  const kwReportMutation = useMutation({
    mutationFn: (kw: string) => kwReportApi.start(kw),
    onSuccess: (res, kw) => {
      const jobId = res.data?.job_id;
      if (jobId) { setKwReportJobId(jobId); setKwReportKeyword(kw); }
      toast.success(`Quick Report 已啟動：${kw}`);
    },
    onError: (err: Error) => toast.error(`Quick Report 失敗: ${err.message}`),
  });

  const handleAnalyze = () => {
    if (!keyword.trim()) { toast.error("請輸入關鍵字"); return; }
    if (!analysisReady) { toast.error("目前帳戶設定不可用，無法提交"); return; }
    analyzeMutation.mutate();
  };

  const handleQuickReport = () => {
    if (!keyword.trim()) { toast.error("請輸入關鍵字"); return; }
    kwReportMutation.mutate(keyword.trim());
  };

  const handleBatchAnalyze = () => {
    const kws = batchKeywords.split("\n").map(k => k.trim()).filter(k => k);
    if (kws.length === 0) { toast.error("請輸入至少一個關鍵字"); return; }
    if (kws.length > 200) { toast.error("最多 200 個關鍵字"); return; }
    if (!analysisReady) { toast.error("目前帳戶設定不可用，無法提交"); return; }
    batchMutation.mutate(kws);
  };

  // Persist form settings to localStorage (restore on mount)
  useEffect(() => {
    const saved = localStorage.getItem("kwSearch_settings");
    if (!saved) return;
    try {
      const s = JSON.parse(saved);
      if (s.projectId) setProjectId(s.projectId);
      if (s.groupId) setGroupId(s.groupId);
      if (s.targetUrl) setTargetUrl(s.targetUrl);
      if (Array.isArray(s.selectedTags)) setSelectedTags(s.selectedTags);
      if (s.locationCode) setLocationCode(s.locationCode);
      if (s.languageCode) setLanguageCode(s.languageCode);
    } catch { /* ignore corrupt data */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem("kwSearch_settings", JSON.stringify({
      projectId, groupId, targetUrl, selectedTags, locationCode, languageCode,
    }));
  }, [projectId, groupId, targetUrl, selectedTags, locationCode, languageCode]);

  // Reset groupId if the saved group no longer exists
  useEffect(() => {
    if (groups.length > 0 && groupId !== "__none__") {
      if (!groups.find((g: KeywordGroup) => g.group_id === groupId)) setGroupId("__none__");
    }
  }, [groups]);

  // Load APP16 projects on mount
  useEffect(() => {
    fetch('/api/app16/projects')
      .then(r => r.json())
      .then((d: {success?: boolean; data?: {id:string;name:string}[]}) => setApp16Projects(Array.isArray(d.data) ? d.data : []))
      .catch(() => {});
  }, []);

  // Load items/groups/tags when APP16 project changes
  const loadApp16Filters = async (projectId: string) => {
    setApp16Items([]); setApp16Groups([]); setApp16Tags([]);
    if (!projectId) return;
    const qs = `?project_id=${encodeURIComponent(projectId)}`;
    const [items, groups, tags] = await Promise.all([
      fetch(`/api/app16/items${qs}`).then(r => r.json()).catch(() => ({})) as Promise<{data?: {id:string;item_name:string;item_type:string}[]}>,
      fetch(`/api/app16/groups${qs}`).then(r => r.json()).catch(() => ({})) as Promise<{data?: {id:string;path:string}[]}>,
      fetch(`/api/app16/tags${qs}`).then(r => r.json()).catch(() => ({})) as Promise<{data?: {id:string;name:string}[]}>,
    ]);
    setApp16Items(items.data ?? []);
    setApp16Groups(groups.data ?? []);
    setApp16Tags(tags.data ?? []);
  };

  // Build status filter options
  const statusOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const req of requests) {
      counts.set(req.status, (counts.get(req.status) || 0) + 1);
    }
    const labels: Record<string, string> = { completed: "已完成", pending: "待處理", processing: "處理中", failed: "失敗" };
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([value, count]) => ({ value, label: labels[value] || value, count }));
  }, [requests]);

  // Filter results
  const filteredRequests = requests.filter(req => {
    if (statusMultiFilter.size > 0 && !statusMultiFilter.has(req.status)) return false;
    if (keywordFilter && !req.target_keyword.toLowerCase().includes(keywordFilter.toLowerCase())) return false;
    return true;
  });

  const kwCount = batchKeywords.split("\n").filter(k => k.trim()).length;

  const statusBadge = (status: string) => {
    const map: Record<string, "default" | "destructive" | "outline" | "secondary"> = {
      completed: "default", failed: "destructive", pending: "outline", processing: "secondary",
    };
    return <Badge variant={map[status] || "outline"}>{status}</Badge>;
  };

  const locationLabel = (code: number) => LOCATION_OPTIONS.find(o => o.value === code)?.label.split(" (")[0] || String(code);

  // Reusable Project selector with inline create
  const ProjectSelector = () => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1"><FolderKanban className="h-3 w-3" />專案</Label>
        <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs"
          onClick={() => setShowNewProject(!showNewProject)}>
          {showNewProject ? "選擇現有" : "+ 建立新專案"}
        </Button>
      </div>
      {showNewProject ? (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input placeholder="新專案名稱" value={newProjectName} onChange={e => setNewProjectName(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && newProjectName.trim()) createProjectMutation.mutate(); }} />
            <Button size="sm" disabled={!newProjectName.trim() || createProjectMutation.isPending}
              onClick={() => createProjectMutation.mutate()}>
              {createProjectMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "建立"}
            </Button>
          </div>
          <Select value={newProjectAccount} onValueChange={setNewProjectAccount}>
            <SelectTrigger><SelectValue placeholder="分析帳戶" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__system_default__">系統預設</SelectItem>
              {accountProfiles.map(profile => (
                <SelectItem key={profile.accountKey} value={profile.accountKey} disabled={!profile.dataforseoConfigured || !profile.llmConfigured}>
                  {profile.accountKey === "personal" ? "Personal（DataForSEO + OpenAI）" : "Company（DataForSEO + CometAPI）"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <Select value={projectId} onValueChange={v => { setProjectId(v); setGroupId("__none__"); }}>
          <SelectTrigger><SelectValue placeholder="全部" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">全部</SelectItem>
            {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
    </div>
  );

  // Reusable Group & Tag section
  const GroupTagSection = () => {
    if (!pid) return null;
    return (
      <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs flex items-center gap-1.5"><Tags className="h-3.5 w-3.5" />存入關鍵字群組（選填）</Label>
            <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs"
              onClick={() => setShowNewGroup(!showNewGroup)}>
              {showNewGroup ? "選擇現有" : "+ 新增群組"}
            </Button>
          </div>
          {showNewGroup ? (
            <div className="flex gap-2">
              <Input className="h-8 text-sm" placeholder="新群組名稱" value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && newGroupName.trim()) createGroupMutation.mutate(); }} />
              <Button size="sm" className="h-8" disabled={!newGroupName.trim() || createGroupMutation.isPending}
                onClick={() => createGroupMutation.mutate()}>
                {createGroupMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "建立"}
              </Button>
            </div>
          ) : (
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger className="h-9"><SelectValue placeholder="不存入群組" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">不存入群組</SelectItem>
                {groups.map(g => (
                  <SelectItem key={g.group_id} value={g.group_id}>
                    {g.name} ({g.keyword_count} 個)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {groupId !== "__none__" && (
          <div className="space-y-2">
            <Label className="text-xs">Tags</Label>
            {/* Selected tags */}
            {selectedTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selectedTags.map(tag => (
                  <Badge key={tag} variant="default" className="text-xs gap-1 pr-1">
                    {tag}
                    <button onClick={() => toggleTag(tag)} className="ml-0.5 hover:text-destructive">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            {/* Available tags from project (clickable) */}
            {projectTags.filter(t => !selectedTags.includes(t)).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                <span className="text-[11px] text-muted-foreground">已有 Tag：</span>
                {projectTags.filter(t => !selectedTags.includes(t)).map(tag => (
                  <Badge key={tag} variant="outline" className="text-xs cursor-pointer hover:bg-primary/10"
                    onClick={() => toggleTag(tag)}>
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
            {/* New tag input */}
            <div className="flex gap-1.5">
              <Input className="h-8 text-sm flex-1" placeholder="輸入新 Tag..."
                value={newTagInput} onChange={e => setNewTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addNewTag(); } }} />
              <Button size="sm" variant="outline" className="h-8" onClick={addNewTag} disabled={!newTagInput.trim()}>
                <Plus className="h-3 w-3 mr-1" />加入
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">分析提交後，關鍵字會自動存入群組並加上所選 Tag</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">關鍵字搜尋</h1>
          <p className="text-muted-foreground">透過 DataForSEO API 分析 Google SERP 排名</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge variant={analysisReady ? "default" : "destructive"}>{accountLabel}</Badge>
          <span className="text-muted-foreground">DataForSEO: {selectedProfile?.dataforseoConfigured ? "已就緒" : "不可用"}</span>
          <span className="text-muted-foreground">LLM: {selectedProfile?.llmConfigured ? `${llmLabel} 已就緒` : `${llmLabel} 不可用`}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card><CardContent className="pt-6"><div className="text-sm text-muted-foreground">總分析次數</div><div className="text-2xl font-bold">{serpStats?.total_requests ?? 0}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-sm text-muted-foreground">已完成</div><div className="text-2xl font-bold text-green-600">{serpStats?.completed ?? 0}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-sm text-muted-foreground">處理中</div><div className="text-2xl font-bold text-blue-600">{serpStats?.pending ?? 0}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-sm text-muted-foreground">總結果數</div><div className="text-2xl font-bold">{serpStats?.total_results ?? 0}</div></CardContent></Card>
      </div>

      {/* Search Form with tabs */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2"><Search className="h-5 w-5" />SERP 分析</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={mode} onValueChange={v => setMode(v as "single" | "batch")}>
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="single">單一分析</TabsTrigger>
              <TabsTrigger value="batch">批量任務</TabsTrigger>
            </TabsList>

            <TabsContent value="single" className="space-y-4">
              <div className="flex items-end gap-3">
                <div className="flex-1 space-y-2">
                  <Label>關鍵字 *</Label>
                  <Input placeholder="輸入要分析的關鍵字" value={keyword}
                    onChange={e => setKeyword(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleAnalyze()} />
                </div>
                <Button onClick={handleAnalyze} disabled={analyzeMutation.isPending || !analysisReady}>
                  {analyzeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                  搜尋分析
                </Button>
                <Button variant="outline" onClick={handleQuickReport} disabled={kwReportMutation.isPending || !analysisReady} title="啟動 T1+T3 Pipeline (Quick Report)">
                  {kwReportMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Quick Report
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><MapPin className="h-3 w-3" />地區</Label>
                  <Select value={locationCode} onValueChange={setLocationCode}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LOCATION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value.toString()}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Languages className="h-3 w-3" />語系</Label>
                  <Select value={languageCode} onValueChange={setLanguageCode}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANGUAGE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <ProjectSelector />
              <div className="space-y-2">
                <Label>目標網址（選填）</Label>
                <Input placeholder="https://your-website.com — 追蹤此網址在搜尋結果中的排名"
                  value={targetUrl} onChange={e => setTargetUrl(e.target.value)} />
              </div>
              {/* Tags row — auto-populated with keyword */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Tag className="h-3 w-3" />Tags 標籤</Label>
                <div className="flex flex-wrap items-center gap-2 p-2 rounded-md border bg-muted/30 min-h-[36px]">
                  {keyword.trim() && (
                    <Badge variant="default" className="text-xs gap-1">
                      {keyword.trim()}
                      <span className="text-[10px] text-muted-foreground/70 ml-0.5">自動</span>
                    </Badge>
                  )}
                  {selectedTags.filter(t => t !== keyword.trim()).map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs gap-1">
                      {tag}
                      <button onClick={() => setSelectedTags(prev => prev.filter(t => t !== tag))} className="ml-0.5 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  <Input
                    className="h-7 w-[140px] border-none bg-transparent text-xs p-0 px-1 focus-visible:ring-0"
                    placeholder="+ 新增 Tag..."
                    value={newTagInput}
                    onChange={e => setNewTagInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter" && newTagInput.trim()) {
                        e.preventDefault();
                        addNewTag();
                      }
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">搜尋關鍵字會自動成為 Tag，搜尋完成後所有任務自動掛上這些 Tags</p>
              </div>
              <GroupTagSection />
              <PipelineOptions page="keyword" selectedStages={pipelineStages} onChange={setPipelineStages} />
            </TabsContent>

            <TabsContent value="batch" className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>關鍵字清單 *</Label>
                  <span className="text-xs text-muted-foreground">{kwCount} 個關鍵字（最多 200 個）</span>
                </div>
                <Textarea rows={6} className="font-mono text-sm"
                  placeholder={"每行一個關鍵字，例如：\nisolation gowns\nmedical isolation gown\ndisposable isolation gown"}
                  value={batchKeywords} onChange={e => setBatchKeywords(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><MapPin className="h-3 w-3" />地區</Label>
                  <Select value={locationCode} onValueChange={setLocationCode}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LOCATION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value.toString()}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Languages className="h-3 w-3" />語系</Label>
                  <Select value={languageCode} onValueChange={setLanguageCode}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANGUAGE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <ProjectSelector />
              <div className="space-y-2">
                <Label>目標網址（選填）（共用）</Label>
                <Input placeholder="https://your-website.com" value={targetUrl} onChange={e => setTargetUrl(e.target.value)} />
              </div>
              {/* Tags row — auto-populated with batch keywords */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Tag className="h-3 w-3" />Tags 標籤</Label>
                <div className="flex flex-wrap items-center gap-2 p-2 rounded-md border bg-muted/30 min-h-[36px]">
                  {batchKeywords.split("\n").map(k => k.trim()).filter(Boolean).slice(0, 5).map(kw => (
                    <Badge key={kw} variant="default" className="text-xs gap-1">
                      {kw}
                      <span className="text-[10px] text-muted-foreground/70 ml-0.5">自動</span>
                    </Badge>
                  ))}
                  {batchKeywords.split("\n").filter(k => k.trim()).length > 5 && (
                    <span className="text-xs text-muted-foreground">+{batchKeywords.split("\n").filter(k => k.trim()).length - 5} 個</span>
                  )}
                  {selectedTags.filter(t => !batchKeywords.split("\n").map(k => k.trim()).includes(t)).map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs gap-1">
                      {tag}
                      <button onClick={() => setSelectedTags(prev => prev.filter(t => t !== tag))} className="ml-0.5 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  <Input
                    className="h-7 w-[140px] border-none bg-transparent text-xs p-0 px-1 focus-visible:ring-0"
                    placeholder="+ 新增 Tag..."
                    value={newTagInput}
                    onChange={e => setNewTagInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter" && newTagInput.trim()) {
                        e.preventDefault();
                        addNewTag();
                      }
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">搜尋關鍵字會自動成為 Tag，搜尋完成後所有任務自動掛上這些 Tags</p>
              </div>
              <GroupTagSection />
              <PipelineOptions page="keyword" selectedStages={pipelineStages} onChange={setPipelineStages} />
              <Button onClick={handleBatchAnalyze} className="w-full"
                disabled={batchMutation.isPending || !analysisReady}>
                {batchMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />處理中...</> : `開始批量分析 (${kwCount} 個)`}
              </Button>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* KW Report Pipeline Progress */}
      {kwReportJobId && (
        <JobProgressCard
          jobId={kwReportJobId}
          keyword={kwReportKeyword}
          onComplete={() => { /* keep card visible after complete */ }}
        />
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>SERP 請求記錄</CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">顯示 {filteredRequests.length} / {requests.length} 筆</span>
              <Button variant="outline" size="sm" onClick={() => queryClient.invalidateQueries({ queryKey: ["serp-requests"] })}>
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 mb-4">
            <Input placeholder="搜尋關鍵字..." className="max-w-[240px]"
              value={keywordFilter} onChange={e => setKeywordFilter(e.target.value)} />
            <MultiSelectFilter label="狀態 Status" options={statusOptions} selected={statusMultiFilter} onChange={setStatusMultiFilter} />
          </div>

          {/* APP16 四維篩選列 */}
          <div className="flex gap-2 flex-wrap mb-4">
            <select
              value={filterApp16Project}
              onChange={e => { setFilterApp16Project(e.target.value); setFilterItem(''); setFilterGroup(''); setFilterTag(''); loadApp16Filters(e.target.value); }}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="">All Projects (APP16)</option>
              {app16Projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>

            <select value={filterItem} onChange={e => setFilterItem(e.target.value)} className="border rounded px-2 py-1 text-sm">
              <option value="">All Items</option>
              {app16Items.map(it => (
                <option key={it.id} value={it.id}>{it.item_type}: {it.item_name}</option>
              ))}
            </select>

            <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)} className="border rounded px-2 py-1 text-sm">
              <option value="">All Groups</option>
              {app16Groups.map(g => (
                <option key={g.id} value={g.id}>{g.path}</option>
              ))}
            </select>

            <select value={filterTag} onChange={e => setFilterTag(e.target.value)} className="border rounded px-2 py-1 text-sm">
              <option value="">All Tags</option>
              {app16Tags.map(t => (
                <option key={t.id} value={t.name}>#{t.name}</option>
              ))}
            </select>
          </div>

          {isLoading ? (
            <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">尚無 SERP 請求</div>
          ) : (
            <div className="space-y-2">
              {filteredRequests.map(req => (
                <div key={req.request_id} className="border rounded-lg">
                  <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
                    onClick={() => setExpandedId(expandedId === req.request_id ? null : req.request_id)}>
                    <div className="flex items-center gap-3">
                      {statusBadge(req.status)}
                      <span className="font-medium">{req.target_keyword}</span>
                      <span className="text-xs text-muted-foreground">帳戶: {requestAccountLabel(req)}</span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" />{locationLabel(req.location_code)}
                      </span>
                      <span className="text-xs text-muted-foreground">{req.language_code}</span>
                      {req.target_url && <span className="text-xs text-muted-foreground truncate max-w-[150px]">{req.target_url}</span>}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span>{req.serp_results_count || 0} 結果</span>
                      {req.cost_dataforseo !== undefined && <span>${Number(req.cost_dataforseo).toFixed(4)} DFS</span>}
                      <span>{new Date(req.created_at).toLocaleString("zh-TW")}</span>
                      <Eye className="h-4 w-4" />
                    </div>
                  </div>
                  {expandedId === req.request_id && expandedResults.length > 0 && (
                    <div className="border-t p-4">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-16">排名</TableHead>
                            <TableHead>標題</TableHead>
                            <TableHead>網域</TableHead>
                            <TableHead>狀態</TableHead>
                            <TableHead>URL</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {expandedResults.slice(0, 20).map(r => (
                            <TableRow key={r.result_id}>
                              <TableCell className="font-bold">{r.rank_group}</TableCell>
                              <TableCell className="max-w-[250px] truncate">{r.title}</TableCell>
                              <TableCell className="text-sm">{r.domain}</TableCell>
                              <TableCell>
                                {r.scraping_task_id ? (
                                  <Badge variant="default" className="text-xs">已爬取</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs">未爬取</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <a href={r.url} target="_blank" rel="noopener noreferrer"
                                  className="text-primary hover:underline text-sm flex items-center gap-1">
                                  {r.url.substring(0, 40)}... <ExternalLink className="h-3 w-3" />
                                </a>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
